import React, { ComponentType, useState, useEffect, lazy } from 'react';
import { PluginManifest, LoadedPlugin } from '../plugins/registry.js';

export interface PanelManifest extends PluginManifest {
  category: 'core' | 'support' | 'analytics' | 'environment' | 'knowledge' | 'automation' | 'onboarding';
  standalone: boolean;
  dependencies: string[];
  apiEndpoints: string[];
  requiredPermissions: string[];
  exportable: boolean;
  moduleSize: 'small' | 'medium' | 'large';
  separationReadiness: 'ready' | 'partial' | 'not-ready';
}

export interface LoadedPanel extends LoadedPlugin {
  manifest: PanelManifest;
  apiClient?: any;
  isolatedState?: boolean;
  exportConfig?: {
    standalone: boolean;
    dependencies: string[];
    buildTarget: string;
  };
}

export interface PanelRegistry {
  allPanels: LoadedPanel[];
  enabledPanels: LoadedPanel[];
  coreModules: LoadedPanel[];
  separableModules: LoadedPanel[];
  getPanel: (id: string) => LoadedPanel | undefined;
  isPanelEnabled: (id: string) => boolean;
  enablePanel: (id: string) => void;
  disablePanel: (id: string) => void;
  exportPanelConfig: (id: string) => any;
  validateSeparation: (id: string) => { ready: boolean; issues: string[] };
  createStandaloneBundle: (id: string) => Promise<any>;
}

class PanelRegistryImpl implements PanelRegistry {
  private panels: Map<string, LoadedPanel> = new Map();
  private enabledPanelIds: Set<string> = new Set();
  private isLoading: boolean = false;

  constructor() {
    this.loadPanels().catch(error => {
      console.error('Failed to initialize panel registry:', error);
    });
  }

  private async loadPanels() {
    try {
      // Add loading state management
      this.isLoading = true;
      
      const demoAnalystPanels = import.meta.env.VITE_DEMO_ANALYST_WORKSTATION === 'true' ? [
        {
          id: 'ticket-dashboard',
          manifest: {
            id: 'ticket-dashboard',
            name: 'Ticket Dashboard',
            version: '1.0.0',
            description: 'Analyst workstation for ticket management and CodeOps integration',
            icon: 'Ticket',
            route: '/ticket-dashboard',
            component: 'TicketDashboardPanel',
            permissions: ['analyst.read', 'analyst.write'],
            dependencies: [],
            category: 'support' as const,
            standalone: false,
            apiEndpoints: ['/api/analyst/support/cases'],
            requiredPermissions: ['analyst.read', 'analyst.write'],
            exportable: true,
            moduleSize: 'medium' as const,
            separationReadiness: 'ready' as const
          },
          component: lazy(() => import('../components/AnalystWorkstation/TicketDashboardPanel').then(module => ({ default: module.TicketDashboardPanel })))
        }
      ] : [];

      const panelConfigs: Array<{ id: string; manifest: PanelManifest; component: ComponentType<any> }> = [
        ...demoAnalystPanels,
        {
          id: 'build-review',
          manifest: {
            id: 'build-review',
            name: 'Build Review',
            version: '1.0.0',
            description: 'Audit results for builds and PRs with coverage and size deltas',
            icon: 'ListChecks',
            route: '/build-review',
            component: 'BuildReviewPanel',
            permissions: ['analytics:read', 'codeops:read'],
            dependencies: ['universalAPIClient'],
            category: 'analytics' as const,
            standalone: true,
            apiEndpoints: ['/api/bees/CursorAuditBee/run'],
            requiredPermissions: ['analytics:read'],
            exportable: true,
            moduleSize: 'small' as const,
            separationReadiness: 'ready' as const
          },
          component: lazy(() => import('../components/BuildReview/BuildReviewPanel').then(m => ({ default: m.BuildReviewPanel || m.default })))
        },
        {
          id: 'control-bridge',
          manifest: {
            id: 'control-bridge',
            name: 'Control Bridge',
            version: '1.0.0',
            description: 'Comprehensive implementation hub for project management, PMO, CostOps, and integrations',
            icon: 'BarChart3',
            route: '/control-bridge',
            component: 'ControlBridgePanel',
            permissions: ['projects:read', 'projects:write', 'pmo:access', 'costops:access', 'integrations:manage'],
            dependencies: [
              'universalAPIClient', 
              'workflowEngine', 
              'storyboardKnowledgeBase', 
              'somaIntegration', 
              'agentAutomation'
            ],
            category: 'support',
            standalone: true,
            apiEndpoints: [
              '/api/projects', 
              '/api/pmo', 
              '/api/costops', 
              '/api/integrations', 
              '/api/storyboards', 
              '/api/knowledge'
            ],
            requiredPermissions: ['projects:admin', 'pmo:admin', 'costops:admin'],
            exportable: true,
            moduleSize: 'large',
            separationReadiness: 'ready'
          },
          component: lazy(() => import('../components/ControlBridge/ControlBridgePanel.js')
            .then(module => ({ default: module.default || module.ControlBridgePanel }))
            .catch(error => {
              console.error('Failed to load Control Bridge Panel:', error);
              return { 
                default: () => (
                  <div className="p-4 text-center text-red-500 bg-red-50 rounded-lg">
                    <h3 className="font-semibold">Control Bridge Panel Unavailable</h3>
                    <p className="text-sm">Failed to load component</p>
                  </div>
                )
              };
            }))
        },
        {
          id: 'knowledge-base',
          manifest: {
            id: 'knowledge-base',
            name: 'Knowledge Base',
            version: '1.0.0',
            description: 'AI-powered knowledge management and contextual assistance',
            icon: 'BookOpen',
            route: '/knowledge-base',
            component: 'KnowledgeBasePanel',
            permissions: ['knowledge:read', 'knowledge:write', 'articles:create'],
            dependencies: ['searchEngine', 'aiAssistant', 'contentManager'],
            category: 'knowledge',
            standalone: true,
            apiEndpoints: ['/api/knowledge', '/api/articles', '/api/search'],
            requiredPermissions: ['knowledge:admin'],
            exportable: true,
            moduleSize: 'medium',
            separationReadiness: 'ready'
          },
          component: lazy(() => import('../components/KnowledgeBase/KnowledgeBasePanel.js')
            .then(module => ({ default: module.default || module.KnowledgeBasePanel }))
            .catch(error => {
              console.error('Failed to load Knowledge Base Panel:', error);
              return { 
                default: () => (
                  <div className="p-4 text-center text-red-500 bg-red-50 rounded-lg">
                    <h3 className="font-semibold">Knowledge Base Panel Unavailable</h3>
                    <p className="text-sm">Failed to load component</p>
                  </div>
                )
              };
            }))
        },
        {
          id: 'analytics-dashboard',
          manifest: {
            id: 'analytics-dashboard',
            name: 'Analytics & Insights',
            version: '1.0.0',
            description: 'Comprehensive analytics and performance metrics dashboard',
            icon: 'BarChart3',
            route: '/analytics',
            component: 'AnalyticsPanel',
            permissions: ['analytics:read', 'metrics:view', 'reports:generate'],
            dependencies: ['metricsEngine', 'chartingLibrary', 'dataProcessor'],
            category: 'analytics',
            standalone: true,
            apiEndpoints: ['/api/analytics', '/api/metrics', '/api/reports'],
            requiredPermissions: ['analytics:admin'],
            exportable: true,
            moduleSize: 'large',
            separationReadiness: 'partial'
          },
          component: lazy(() => import('../components/Analytics/AnalyticsPanel.js')
            .then(module => ({ default: module.default || module.AnalyticsPanel }))
            .catch(error => {
              console.error('Failed to load Analytics Panel:', error);
              return { 
                default: () => (
                  <div className="p-4 text-center text-red-500 bg-red-50 rounded-lg">
                    <h3 className="font-semibold">Analytics Panel Unavailable</h3>
                    <p className="text-sm">Failed to load component</p>
                  </div>
                )
              };
            }))
        },
        {
          id: 'environment-manager',
          manifest: {
            id: 'environment-manager',
            name: 'Environment Manager',
            version: '1.0.0',
            description: 'Multi-platform environment and secrets management',
            icon: 'Settings',
            route: '/environment',
            component: 'EnvironmentPanel',
            permissions: ['env:read', 'env:write', 'secrets:manage'],
            dependencies: ['envBootstrapBee', 'secretsManager', 'platformAdapters'],
            category: 'environment',
            standalone: true,
            apiEndpoints: ['/api/environment', '/api/secrets', '/api/platforms'],
            requiredPermissions: ['env:admin', 'secrets:admin'],
            exportable: true,
            moduleSize: 'medium',
            separationReadiness: 'ready'
          },
          component: lazy(() => import('../components/Environment/EnvironmentPanel.js')
            .then(module => ({ default: module.default || module.EnvironmentPanel }))
            .catch(error => {
              console.error('Failed to load Environment Panel:', error);
              return { 
                default: () => (
                  <div className="p-4 text-center text-red-500 bg-red-50 rounded-lg">
                    <h3 className="font-semibold">Environment Panel Unavailable</h3>
                    <p className="text-sm">Failed to load component</p>
                  </div>
                )
              };
            }))
        },
        {
          id: 'automation-workflows',
          manifest: {
            id: 'automation-workflows',
            name: 'Automation & Workflows',
            version: '1.0.0',
            description: 'Workflow orchestration and automation management',
            icon: 'Zap',
            route: '/automation',
            component: 'AutomationPanel',
            permissions: ['automation:read', 'automation:write', 'workflows:execute'],
            dependencies: ['workflowEngine', 'automationBees', 'orchestrator'],
            category: 'automation',
            standalone: true,
            apiEndpoints: ['/api/workflows', '/api/automation', '/api/orchestration'],
            requiredPermissions: ['automation:admin'],
            exportable: true,
            moduleSize: 'large',
            separationReadiness: 'partial'
          },
          component: lazy(() => import('../components/Automation/AutomationPanel.js')
            .then(module => ({ default: module.default || module.AutomationPanel }))
            .catch(error => {
              console.error('Failed to load Automation Panel:', error);
              return { 
                default: () => (
                  <div className="p-4 text-center text-red-500 bg-red-50 rounded-lg">
                    <h3 className="font-semibold">Automation Panel Unavailable</h3>
                    <p className="text-sm">Failed to load component</p>
                  </div>
                )
              };
            }))
        },
        {
          id: 'engineering-bridge',
          manifest: {
            id: 'engineering-bridge',
            name: 'Engineering Bridge',
            version: '1.0.0',
            description: 'Conversion quality, throughput, and issue-triage metrics for engineering teams',
            icon: 'Code',
            route: '/engineering-bridge',
            component: 'EngineeringBridgePage',
            permissions: ['engineer:read', 'analyst:read', 'conversion_manager:read', 'admin:read'],
            dependencies: ['universalAPIClient', 'metricsEngine', 'dataProcessor'],
            category: 'analytics',
            standalone: true,
            apiEndpoints: [
              '/api/engineering/triage/summary',
              '/api/engineering/precision/overview',
              '/api/engineering/dq/summary',
              '/api/engineering/qa/overview',
              '/api/conversionops/runs/summary',
              '/api/codeops/dora',
              '/api/codeops/prs/summary',
              '/api/ops/runbooks',
              '/api/ops/feature-flags'
            ],
            requiredPermissions: ['engineer:read', 'analyst:read'],
            exportable: true,
            moduleSize: 'large',
            separationReadiness: 'ready'
          },
          component: lazy(() => import('../pages/EngineeringBridgePage')
            .then(module => ({ default: module.default }))
            .catch(error => {
              console.error('Failed to load Engineering Bridge Page:', error);
              return { 
                default: () => (
                  <div className="p-4 text-center text-red-500 bg-red-50 rounded-lg">
                    <h3 className="font-semibold">Engineering Bridge Unavailable</h3>
                    <p className="text-sm">Failed to load component</p>
                  </div>
                )
              };
            }))
        },
        {
          id: 'onboarding-cohorts',
          manifest: {
            id: 'onboarding-cohorts',
            name: 'Onboarding & Training',
            version: '1.0.0',
            description: 'Cohort-based onboarding and interactive training modules',
            icon: 'GraduationCap',
            route: '/onboarding',
            component: 'OnboardingPanel',
            permissions: ['onboarding:read', 'onboarding:write', 'training:access'],
            dependencies: ['syntaraStudio', 'arcCanvas', 'progressTracker'],
            category: 'onboarding',
            standalone: true,
            apiEndpoints: ['/api/onboarding', '/api/training', '/api/cohorts'],
            requiredPermissions: ['onboarding:admin'],
            exportable: true,
            moduleSize: 'medium',
            separationReadiness: 'partial'
          },
          component: lazy(() => import('../components/Onboarding/OnboardingPanel.js')
            .then(module => ({ default: module.default || module.OnboardingPanel }))
            .catch(error => {
              console.error('Failed to load Onboarding Panel:', error);
              return { 
                default: () => (
                  <div className="p-4 text-center text-red-500 bg-red-50 rounded-lg">
                    <h3 className="font-semibold">Onboarding Panel Unavailable</h3>
                    <p className="text-sm">Failed to load component</p>
                  </div>
                )
              };
            }))
        },
        {
          id: 'etl-wizard',
          manifest: {
            id: 'etl-wizard',
            name: 'ETL Wizard',
            version: '1.0.0',
            description: 'Data extraction, transformation, and loading wizard for HiveGrid',
            icon: 'Database',
            route: '/etl-wizard',
            component: 'ETLWizardPanel',
            permissions: ['etl:read', 'etl:write', 'data:manage'],
            dependencies: ['hivegrid', 'etlEngine', 'dataConnectors'],
            category: 'automation',
            standalone: true,
            apiEndpoints: ['/api/hivegrid/etl/preview', '/api/hivegrid/etl/profile', '/api/hivegrid/etl/deploy'],
            requiredPermissions: ['etl:admin'],
            exportable: true,
            moduleSize: 'medium',
            separationReadiness: 'ready'
          },
          component: lazy(() => import('../components/ControlBridge/ETLWizard/ETLWizardPanel.js')
            .then(module => ({ default: module.default || module.ETLWizardPanel }))
            .catch(error => {
              console.error('Failed to load ETL Wizard Panel:', error);
              return { 
                default: () => (
                  <div className="p-4 text-center text-red-500 bg-red-50 rounded-lg">
                    <h3 className="font-semibold">ETL Wizard Panel Unavailable</h3>
                    <p className="text-sm">Failed to load component</p>
                  </div>
                )
              };
            }))
        }
      ];

      for (const config of panelConfigs) {
        const loadedPanel: LoadedPanel = {
          manifest: config.manifest,
          component: config.component as ComponentType<any>,
          isEnabled: true,
          isolatedState: config.manifest.standalone,
          exportConfig: {
            standalone: config.manifest.standalone,
            dependencies: config.manifest.dependencies,
            buildTarget: `standalone-${config.id}`
          }
        };

        this.panels.set(config.id, loadedPanel);
        this.enabledPanelIds.add(config.id);
      }

      console.log('Panel registry initialized with panels:', Array.from(this.panels.keys()));
      this.isLoading = false;
    } catch (error) {
      console.error('Failed to load panels:', error);
      this.isLoading = false;
      // Initialize with empty registry to prevent complete failure
      this.panels = new Map();
      this.enabledPanelIds = new Set();
    }
  }

  get allPanels(): LoadedPanel[] {
    if (!this.panels) {
      return [];
    }
    return Array.from(this.panels.values());
  }

  get enabledPanels(): LoadedPanel[] {
    return this.allPanels.filter(panel => this.enabledPanelIds.has(panel.manifest.id));
  }

  get coreModules(): LoadedPanel[] {
    return this.allPanels.filter(panel => panel.manifest.category === 'core');
  }

  get separableModules(): LoadedPanel[] {
    return this.allPanels.filter(panel => 
      panel.manifest.standalone && 
      panel.manifest.separationReadiness === 'ready'
    );
  }

  getPanel(id: string): LoadedPanel | undefined {
    if (!this.panels) {
      return undefined;
    }
    return this.panels.get(id);
  }

  isPanelEnabled(id: string): boolean {
    return this.enabledPanelIds.has(id);
  }

  enablePanel(id: string): void {
    if (!this.panels) {
      return;
    }
    if (this.panels.has(id)) {
      this.enabledPanelIds.add(id);
      const panel = this.panels.get(id)!;
      panel.isEnabled = true;
    }
  }

  disablePanel(id: string): void {
    this.enabledPanelIds.delete(id);
    if (!this.panels) {
      return;
    }
    const panel = this.panels.get(id);
    if (panel) {
      panel.isEnabled = false;
    }
  }

  exportPanelConfig(id: string): any {
    if (!this.panels) {
      throw new Error('Panel registry not initialized');
    }
    const panel = this.panels.get(id);
    if (!panel || !panel.manifest.exportable) {
      throw new Error(`Panel ${id} is not exportable`);
    }

    return {
      manifest: panel.manifest,
      exportConfig: panel.exportConfig,
      dependencies: panel.manifest.dependencies,
      apiEndpoints: panel.manifest.apiEndpoints,
      buildInstructions: {
        framework: 'react',
        bundler: 'vite',
        styling: 'tailwind',
        target: 'standalone-spa'
      },
      separationGuide: {
        steps: [
          'Extract component and dependencies',
          'Create standalone package.json',
          'Configure independent build system',
          'Setup isolated API client',
          'Implement authentication bridge',
          'Test standalone functionality'
        ]
      }
    };
  }

  validateSeparation(id: string): { ready: boolean; issues: string[] } {
    if (!this.panels) {
      return { ready: false, issues: ['Panel registry not initialized'] };
    }
    
    const panel = this.panels.get(id);
    if (!panel) {
      return { ready: false, issues: [`Panel ${id} not found`] };
    }

    const issues: string[] = [];

    if (!panel.manifest.standalone) {
      issues.push('Panel is not marked as standalone');
    }

    if (panel.manifest.separationReadiness !== 'ready') {
      issues.push(`Separation readiness is ${panel.manifest.separationReadiness}, not ready`);
    }

    if (panel.manifest.dependencies.length === 0) {
      issues.push('No dependencies defined - may indicate incomplete configuration');
    }

    if (panel.manifest.apiEndpoints.length === 0) {
      issues.push('No API endpoints defined - panel may not function independently');
    }

    return {
      ready: issues.length === 0,
      issues
    };
  }

  async createStandaloneBundle(id: string): Promise<any> {
    if (!this.panels) {
      throw new Error('Panel registry not initialized');
    }
    const panel = this.panels.get(id);
    if (!panel) {
      throw new Error(`Panel ${id} not found`);
    }

    const validation = this.validateSeparation(id);
    if (!validation.ready) {
      throw new Error(`Panel ${id} is not ready for separation: ${validation.issues.join(', ')}`);
    }

    return {
      bundleId: `${id}-standalone-${Date.now()}`,
      manifest: panel.manifest,
      buildConfig: {
        entry: `src/panels/${id}/index.tsx`,
        output: `dist/${id}-standalone`,
        dependencies: panel.manifest.dependencies,
        externals: ['react', 'react-dom'],
        plugins: ['@vitejs/plugin-react', 'vite-plugin-tailwindcss']
      },
      deploymentConfig: {
        type: 'spa',
        routing: 'hash',
        baseUrl: `/${id}`,
        apiProxy: panel.manifest.apiEndpoints
      },
      instructions: [
        `cd standalone-builds/${id}`,
        'npm install',
        'npm run build',
        'npm run deploy'
      ]
    };
  }
}

export const panelRegistry = new PanelRegistryImpl();

export const usePanelRegistry = (): PanelRegistry & { isLoading: boolean } => {
  const [isLoading, setIsLoading] = useState(true);
  const [, forceUpdate] = useState({});
  
  useEffect(() => {
    const checkPanels = () => {
      if (panelRegistry.allPanels.length > 0) {
        setIsLoading(false);
        forceUpdate({});
      } else {
        setTimeout(checkPanels, 100);
      }
    };
    
    checkPanels();
  }, []);
  
  return {
    allPanels: panelRegistry.allPanels,
    enabledPanels: panelRegistry.enabledPanels,
    coreModules: panelRegistry.coreModules,
    separableModules: panelRegistry.separableModules,
    getPanel: panelRegistry.getPanel.bind(panelRegistry),
    isPanelEnabled: panelRegistry.isPanelEnabled.bind(panelRegistry),
    enablePanel: panelRegistry.enablePanel.bind(panelRegistry),
    disablePanel: panelRegistry.disablePanel.bind(panelRegistry),
    exportPanelConfig: panelRegistry.exportPanelConfig.bind(panelRegistry),
    validateSeparation: panelRegistry.validateSeparation.bind(panelRegistry),
    createStandaloneBundle: panelRegistry.createStandaloneBundle.bind(panelRegistry),
    isLoading
  };
};

export const useSeparableModules = () => {
  const registry = usePanelRegistry();
  
  return {
    separableModules: registry.separableModules,
    validateSeparation: registry.validateSeparation,
    exportConfig: registry.exportPanelConfig,
    createBundle: registry.createStandaloneBundle
  };
};
